import { useState } from "react";
import { FetchResult, gql, useMutation } from "@apollo/client";
import { ExecutionMethod } from "api/enums";
import { TransactionType } from "api/transactions/enums";
import {
  LocalTradeOrderDetails,
  useLocalTradeStorageMutation,
} from "hooks/useLocalTradeStorageMutation";
import { toast } from "react-toastify";
import { useModifiedTranslation } from "../../hooks/useModifiedTranslation";
import { useUniqueReference } from "../../hooks/useUniqueReference";


const IMPORT_LIMITED_TRADE_ORDER_MUTATION = gql`
  mutation ImportLimitedTradeOrder(
    $portfolioShortName: String
    $transactionDate: String
    $securityCode: String
    $transactionTypeCode: String
    $units: Float
    $unitPrice: Float
    $tradeAmount: Float
    $currency: String
    $reference: String
    $executionMethod: Int
    $tags: String
    $autoFxRate: Boolean
  ) {
      importLimitedTradeOrder(
        limitedTradeOrder: {
          parentPortfolio: $portfolioShortName
          transactionDate: $transactionDate
          security: $securityCode
          type: $transactionTypeCode
          status: "4"
          amount: $units
          unitPrice: $unitPrice
          tradeAmount: $tradeAmount
          currency: $currency
          account: "AUTO"
          reference: $reference
          executionMethod: $executionMethod
          tags: $tags
          autoFxRate: $autoFxRate
        }
      )
  }
`;


const IMPORT_LIMITED_TRADE_ORDER_MUTATION_AUTO_UNIT_PRICE = gql`
  mutation ImportLimitedTradeOrder(
    $portfolioShortName: String
    $transactionDate: String
    $securityCode: String
    $transactionTypeCode: String
    $units: Float
    $autoUnitPrice: Boolean
    $tradeAmount: Float
    $currency: String
    $reference: String
    $executionMethod: Int
    $tags: String
    $autoFxRate: Boolean
  ) {
      importLimitedTradeOrder(
        limitedTradeOrder: {
          parentPortfolio: $portfolioShortName
          transactionDate: $transactionDate
          security: $securityCode
          type: $transactionTypeCode
          status: "4"
          amount: $units
          autoUnitPrice: $autoUnitPrice
          tradeAmount: $tradeAmount
          currency: $currency
          account: "AUTO"
          reference: $reference
          executionMethod: $executionMethod
          tags: $tags
          autoFxRate: $autoFxRate
        }
      )
  }
`;

interface ImportLimitedTradeOrderQueryVariables {
  portfolioShortName: string;
  transactionDate: Date;
  securityCode: string;
  transactionTypeCode: string;
  units?: number;
  unitPrice?: number;
  autoUnitPrice?: boolean;
  tradeAmount?: number;
  currency?: string;
  reference: string;
  executionMethod: ExecutionMethod;
  tags?: string;
  autoFxRate?: boolean;
  isInSubscrPortalGroup?: boolean;
}

const errorStatus = "ERROR" as const;

interface ImportLimitedTradeOrderQueryResponse {
  importLimitedTradeOrder: ({
    importStatus: "OK" | typeof errorStatus;
  } & unknown)[];
}

export type TradeType = "sell" | "buy" | "redemption" | "subscription";

export const useTrade = (
  newTradeOrder: Omit<
    ImportLimitedTradeOrderQueryVariables,
    | "transactionTypeCode"
    | "transactionDate"
    | "reference"
    | "portfolioShortName"
  > &
    Omit<LocalTradeOrderDetails, "tradeType" | "reference"> & {
      tradeType: TradeType;
    }
) => {
  let setAutoPrice = true;

  if (newTradeOrder.tags?.includes("apitradeorder.Order type-Limit") &&newTradeOrder.unitPrice && newTradeOrder.unitPrice > 0) {
    setAutoPrice = false;
  }

  const { t } = useModifiedTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [handleAPITrade] = useMutation<
    ImportLimitedTradeOrderQueryResponse,
    ImportLimitedTradeOrderQueryVariables
  >(setAutoPrice ? IMPORT_LIMITED_TRADE_ORDER_MUTATION_AUTO_UNIT_PRICE : IMPORT_LIMITED_TRADE_ORDER_MUTATION);

  const saveToLocalTradeOrders = useLocalTradeStorageMutation();
  const getUniqueReference = useUniqueReference();

  const handleTrade = async () => {
    setSubmitting(true);
    try {
      const {
        tradeType,
        portfolio,
      } = newTradeOrder;
      if (!portfolio) {
        return;
      }
      const transactionReference = getUniqueReference();

      const variables = {
          ...newTradeOrder,
          tags: `${newTradeOrder.tags}`,
          autoFxRate: true,
          transactionDate: new Date(),
          transactionTypeCode: getTradeTypeForAPI(tradeType, newTradeOrder.isInSubscrPortalGroup),
          reference: transactionReference,
          portfolioShortName: portfolio.shortName,
      };

      if (setAutoPrice) {
        variables.autoUnitPrice = true;
      } else {
        variables.unitPrice = newTradeOrder.unitPrice;
      }


      const apiResponse = await handleAPITrade({
        variables: variables,
      });

      handleBadAPIResponse(apiResponse);

      await saveToLocalTradeOrders({
        ...newTradeOrder,
        reference: transactionReference,
      });

      toast.success(t("tradingModal.createTradeSuccess"), { autoClose: 3000 });
      setSubmitting(false);
      return apiResponse;
    } catch (e: unknown) {
      toast.error(t("tradingModal.createTradeError"), {
        style: { whiteSpace: "pre-line" },
      });
      setSubmitting(false);
      return null;
    }
  };

  return { handleTrade, submitting };
};

const handleBadAPIResponse = (
  apiResponse: FetchResult<
    ImportLimitedTradeOrderQueryResponse,
    Record<string, unknown>,
    Record<string, unknown>
  >
) => {
  if (!apiResponse.data || !apiResponse.data.importLimitedTradeOrder?.[0]) {
    throw new Error("Empty response");
  }

  if (apiResponse.data.importLimitedTradeOrder[0].importStatus === errorStatus) {
    let errorMessage = "Bad request: \n";
    Object.entries(apiResponse.data.importLimitedTradeOrder[0]).forEach(
      ([key, value]) => {
        if (value.includes("ERROR") && key !== "importStatus") {
          errorMessage += `${key}: ${value}; \n`;
        }
      }
    );
    throw new Error(errorMessage);
  }
};

const getTradeTypeForAPI = (tradeType: TradeType, isInSubscrPortalGroup?: boolean) => {
  switch (tradeType) {
    case "buy": {
      return isInSubscrPortalGroup ? TransactionType.SUBSCRIPTION : TransactionType.BUY;
    }
    case "sell": {
      return TransactionType.SELL;
    }
    case "subscription": {
      return TransactionType.SUBSCRIPTION;
    }
    case "redemption": {
      return TransactionType.REDEMPTION;
    }
    default: {
      throw new Error("Impossible API trade type");
    }
  }
};
